
import { Download, FileArchive, ShieldCheck, User, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
  fetchEvidenceBase64,
  fetchReportExecutiveSummary,
  streamAssessmentReport,
  streamReport,
  type ReportProgress,
} from "./services/reportApi";
import { useToastStore } from "../../../stores/toastStore";
import { PageShell } from "../../components/layout/PageShell";
import { Button } from "../../components/ui/button";
import { RegoWaterShield } from "./components/ReportWaterLoader";
import { mapReportData } from "./utils/mapReportData";
import type { ReportPayload } from "./types/reportTypes";
import { downloadReport } from "./services/downloadReport";
import {
  buildAuditReportHtml,
  type ExecSummaryState,
  type ReportAudience,
} from "./utils/buildAuditReportHtml";

export function AuditReportDetail() {
  // `assessmentId` is present only on /audit-report/:sspId/assessment/:assessmentId
  // (one assessment plan's report); absent on the SSP-wide route.
  const { sspId, assessmentId } = useParams<{
    sspId: string;
    assessmentId: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Audience is picked on the reports list's Eye icon and handed over on the
  // query string (?audience=auditor&assessmentId=…), so the Download buttons
  // here download straight away instead of asking again.
  const urlAudience: ReportAudience =
    searchParams.get("audience") === "system_owner" ? "system_owner" : "auditor";
  // Plan chosen in the picker. When it differs from the plan this page already
  // streamed, an auditor download re-streams it so the file is plan-correct.
  const urlAssessmentId = searchParams.get("assessmentId") || undefined;
  const urlPlanTitle = searchParams.get("planTitle") || undefined;

  const [payload, setPayload] = useState<ReportPayload | null>(null);
  const [progress, setProgress] = useState<ReportProgress>({
    percent: 0,
    phase: "Starting...",
  });
  const [streamDone, setStreamDone] = useState(false);
  const [execState, setExecState] = useState<ExecSummaryState>("loading");
  const [execText, setExecText] = useState("");
  const [downloading, setDownloading] = useState<"html" | "evidence" | null>(
    null,
  );
  const [previewAudience, setPreviewAudience] = useState<ReportAudience>("auditor");
  // Accumulates each `section` event's payload (shallow + nested `report` merge).
  const rawRef = useRef<Record<string, any>>({});
  const hasAnnouncedLoaded = useRef(false);
  const mergeRaw = (chunk: unknown) => {
    const prev = rawRef.current;
    const c = (chunk ?? {}) as Record<string, any>;
    rawRef.current = {
      ...prev,
      ...c,
      report: { ...(prev.report ?? {}), ...(c.report ?? {}) },
    };
  };

  // Streams the main payload + fetches the exec summary in parallel; resets on sspId change.
  useEffect(() => {
    let alive = true;
    if (!sspId) {
      navigate("/report");
      return;
    }

    setPayload(null);
    setProgress({ percent: 0, phase: "Starting..." });
    setStreamDone(false);
    setExecState("loading");
    setExecText("");
    rawRef.current = {};
    hasAnnouncedLoaded.current = false;

    const ctrl = new AbortController();

    const onProgress = (p: ReportProgress) => {
      if (alive) setProgress(p);
    };
    const onSection = (_name: string, sectionPayload: unknown) => {
      if (!alive) return;
      mergeRaw(sectionPayload);
      setPayload(
        mapReportData(rawRef.current, sspId, {
          scopedPlanId: planId,
          scopedPlanTitle: urlPlanTitle,
        }),
      );
    };

    // Both endpoints speak the same event contract, so only the source differs.
    // `planId` covers both routes that scope to one plan: the /assessment/:id
    // path segment, and ?assessmentId= handed over by the Eye-icon picker.
    const planId = assessmentId || urlAssessmentId;
    const streamed = planId
      ? streamAssessmentReport(
        sspId,
        planId,
        onProgress,
        ctrl.signal,
        onSection,
      )
      : streamReport(sspId, onProgress, ctrl.signal, onSection);

    streamed
      .then((raw) => {
        if (!alive) return;
        mergeRaw(raw); // `complete` patches in anything not yet covered by a section event
        if ((import.meta as any).env?.DEV) {
          console.info(
            "[audit-report] complete event top-level keys —",
            Object.keys(rawRef.current ?? {}),
          );
          console.info(
            "[audit-report] complete event report keys —",
            Object.keys(rawRef.current?.report ?? {}),
          );
        }
        setPayload(
          mapReportData(rawRef.current, sspId, {
            scopedPlanId: planId,
            scopedPlanTitle: urlPlanTitle,
          }),
        );
        setProgress({ percent: 100, phase: "Complete" });
        setStreamDone(true);
      })
      .catch((err) => {
        if (!alive || err?.name === "AbortError") return; // expected on unmount/sspId change
        useToastStore
          .getState()
          .showError(
            "Report unavailable",
            "Could not load the report. Please try again.",
          );
        navigate("/report");
      });

    // Only the SSP-wide endpoint is known for the summary card; the
    // assessment-scoped report reuses it and tolerates a failure.
    fetchReportExecutiveSummary(sspId)
      .then((text) => {
        if (!alive) return;
        setExecText(text || "");
        setExecState("ready");
      })
      .catch(() => {
        if (!alive) return;
        setExecState("error"); // report still renders; summary card shows an error
      });

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [sspId, assessmentId, urlAssessmentId]);

  // Adopt the audience the Eye-icon picker handed over, so the iframe renders in
  // that audience from the first paint rather than flashing the default.
  useEffect(() => {
    setPreviewAudience(urlAudience);
  }, [urlAudience]);

  // The iframe has no auth token, so it asks us to fetch evidence files and posts the bytes back.
  useEffect(() => {
    const onMsg = async (e: MessageEvent) => {
      // Validate that message originates from the same origin or internal srcdoc iframe
      if (e.origin !== window.location.origin && e.origin !== "null") return;
      const d = e.data as { type?: string; resourceId?: string } | null;
      if (!d || d.type !== "rego-evidence-request" || !d.resourceId) return;
      const src = e.source as Window | null;
      if (!src) return;
      const rid = d.resourceId;

      let payload: { type: string; resourceId: string; fileName?: string; contentType?: string; base64?: string };
      try {
        const r = await fetchEvidenceBase64(rid);
        payload = { type: "rego-evidence-result", resourceId: rid, ...r };
      } catch {
        payload = { type: "rego-evidence-result", resourceId: rid };
      }
      src.postMessage(payload, window.location.origin);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const pct = Math.min(100, Math.max(0, Math.round(progress.percent)));

  // Renders only when stream is fully done, exec summary loaded, and progress reaches 100%
  const fullyReady = streamDone && execState !== "loading" && pct >= 100;
  const ready = fullyReady && !!payload;

  useEffect(() => {
    if (fullyReady && !hasAnnouncedLoaded.current) {
      hasAnnouncedLoaded.current = true;
      useToastStore.getState().showSuccess("Success", "Report loaded");
    }
  }, [fullyReady]);
  const html = useMemo(
    () =>
      ready && payload
        ? buildAuditReportHtml(payload, {
          execSummary: { state: execState, text: execText },
          audience: previewAudience,
        })
        : "",
    [ready, payload, execState, execText, previewAudience],
  );

  // The audience was already chosen on the reports list, so the buttons just
  // download in it. An auditor report is plan-scoped: `pendingPlanId` is the
  // plan handed over via ?assessmentId. When this page streamed a *different*
  // plan (or the SSP-wide report), an auditor download re-streams the chosen one
  // so the saved file is plan-correct.
  const pendingPlanId =
    previewAudience === "auditor" && urlAssessmentId && urlAssessmentId !== assessmentId
      ? urlAssessmentId
      : undefined;

  // withEvidence adds a fetch per evidence file to embed in the zip; otherwise no network calls.
  const handleDownload = async (withEvidence: boolean) => {
    const audience = previewAudience;
    if (downloading) return;
    if (!payload && !pendingPlanId) return;
    if (!pendingPlanId && !fullyReady) return;
    setDownloading(withEvidence ? "evidence" : "html");
    try {
      let source = payload;
      let summaryState = execState;
      let summaryText = execText;

      if (pendingPlanId) {
        if (!sspId) return;
        // No-op progress callback: the header button already shows
        // "Preparing...", so surface nothing extra.
        const raw = await streamAssessmentReport(
          sspId,
          pendingPlanId,
          () => {},
        );
        source = mapReportData(raw, sspId, {
          scopedPlanId: pendingPlanId,
          scopedPlanTitle: urlPlanTitle,
        });
        // There is no assessment-scoped executive-summary endpoint, so fall back
        // to the SSP-wide one already on hand rather than blanking the card.
        if (summaryState === "error") summaryState = "ready";
        if (!summaryText) {
          summaryText = await fetchReportExecutiveSummary(sspId).catch(() => "");
        }
      }

      if (!source) return;
      const res = await downloadReport(source, {
        execState: summaryState,
        execText: summaryText,
        withEvidence,
        audience,
      });
      if (res.evidenceTotal > 0 && res.evidenceFetched < res.evidenceTotal) {
        useToastStore
          .getState()
          .showError(
            "Evidence",
            `Only ${res.evidenceFetched} of ${res.evidenceTotal} evidence files could be fetched.`,
          );
      }
    } catch {
      useToastStore
        .getState()
        .showError("Download", "Could not generate the report download.");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <PageShell className="flex flex-col h-[calc(100vh-7rem)] space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/report")}
          className="size-auto flex-shrink-0 p-1.5 rounded-md text-slate-400 hover:text-foreground hover:bg-accent"
          aria-label="Close report"
          title="Close report"
        >
          <X className="size-5" />
        </Button>
        <div className="flex items-center gap-3">
          {ready && !fullyReady && (
            <div
              className="flex items-center gap-2 text-xs text-slate-500"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <span className="relative flex h-1.5 w-24 overflow-hidden rounded-full bg-slate-200">
                <span
                  className="absolute inset-y-0 left-0 rounded-full bg-indigo-500 transition-all"
                  style={{ width: `${pct}%` }}
                />
              </span>
              <span className="tabular-nums">
                {progress.phase} {pct}%
              </span>
            </div>
          )}

          <div className="flex items-center gap-2">
            {/* Read-only badge: the audience was chosen on the reports list
                (Eye icon), so it is shown here for context, not re-picked. */}
            <span
              className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-2 text-[11px] font-bold text-indigo-700 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-indigo-300"
              title="Audience selected on the reports list"
            >
              {previewAudience === "auditor" ? (
                <ShieldCheck className="size-3.5" />
              ) : (
                <User className="size-3.5" />
              )}
              {previewAudience === "auditor" ? "Auditor" : "System Owner"}
            </span>
            <Button
              onClick={() => handleDownload(false)}
              disabled={!fullyReady || !!downloading}
              className="h-auto px-4 py-2 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              title={`Download the ${previewAudience === "auditor" ? "auditor" : "system owner"} report`}
            >
              <Download className="size-3.5" />
              {downloading === "html" ? "Preparing..." : "Download Report"}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleDownload(true)}
              disabled={!fullyReady || !!downloading}
              className="h-auto px-4 py-2 text-xs font-bold border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50"
              title={`Download the zip with evidence embedded (${previewAudience === "auditor" ? "auditor" : "system owner"})`}
            >
              <FileArchive className="size-3.5" />
              {downloading === "evidence" ? "Preparing..." : "Download with Evidence"}
            </Button>
          </div>
        </div>
      </div>
      {ready && payload ? (
        <iframe
          title={`Audit Report - ${payload.meta.sspName}`}
          srcDoc={html}
          className="flex-1 w-full rounded-lg border border-slate-200 bg-white"
        />
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center gap-5 rounded-lg border border-slate-200 bg-white">
          <div
            className="relative w-32 h-32"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <RegoWaterShield pct={pct} />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-600">
              {progress.phase}{" "}
              <span className="font-semibold tabular-nums text-indigo-600">
                {pct}%
              </span>
            </p>
            <p className="mt-1 text-xs text-slate-400">Generating report...</p>
          </div>
        </div>
      )}
    </PageShell>
  );
}
